import './styles/ProductList.scss';

import { Grid } from '@mui/material';
import { Product } from '@thro-types/leihbar';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import { useLeihbarCategoriesQuery, useLocationsQuery, useProductsQuery } from 'api/hooks/leihbar';
import { CreateButton, Filter, TilesOrListView, TypedGridColumns } from 'app/components';
import { translations } from 'locales/i18n';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { usePermissionToCreate } from '../../helper';
import { ProductCard } from '../ProductCard';

enum SortOrder {
  ASC = 'asc',
  DESC = 'desc'
}

export const ProductList = () => {
  const { t } = useTranslation();
  const currentURL = useCurrentURL();
  const ableToCreate = usePermissionToCreate();
  const navigate = useNavigate();

  const titleFilterInputState = useState('');
  const locationFilterInputState = useState('');
  const categoryFilterInputState = useState('');
  const lenderGroupFilterInputState = useState('');
  const showMineFilterState = useState(false);

  const { data: products } = useProductsQuery({
    params: {
      sort: 'name',
      sortOrder: SortOrder.ASC,
      title: titleFilterInputState[0]?.length > 0 ? titleFilterInputState[0] : undefined,
      location: locationFilterInputState[0]?.length > 0 ? locationFilterInputState[0] : undefined,
      category: categoryFilterInputState[0]?.length > 0 ? categoryFilterInputState[0] : undefined,
      lenderGroup:
        typeof lenderGroupFilterInputState[0] === 'number'
          ? lenderGroupFilterInputState[0]
          : undefined,
      showMine: showMineFilterState[0]
    }
  });

  const { data: entitlementGroups } = useEntitlementGroupsQuery(true, {
    params: { relationType: 'LEIHBAR' }
  });
  const { data: locations } = useLocationsQuery();
  const { data: categories } = useLeihbarCategoriesQuery();

  const filterLocations = () => {
    return locations ? locations.map(l => ({ label: l.name, name: l.name })) : [];
  };

  const filterCategories = () => {
    return categories ? categories.map(c => ({ label: c.name, name: c.name })) : [];
  };

  const filterDomains = () => {
    return entitlementGroups
      ? entitlementGroups.data.map(c => ({
          label: c.name,
          name: c.id
        }))
      : [];
  };

  const filters: Filter[] = [
    {
      field: 'title',
      label: t(translations.common.search()),
      stateHandler: titleFilterInputState
    },
    {
      field: 'category',
      label: t(translations.common.category()),
      select: {
        options: [{ label: '---', name: null }, ...filterCategories()],
        stateHandler: categoryFilterInputState
      }
    },
    {
      field: 'location',
      label: t(translations.common.location()),
      select: {
        options: [{ label: '---', name: null }, ...filterLocations()],
        stateHandler: locationFilterInputState
      }
    },
    {
      field: 'lenderGroup',
      label: t(translations.pages.leihbar.product.domain()),
      select: {
        options: [{ label: '---', name: null }, ...filterDomains()],
        stateHandler: lenderGroupFilterInputState
      }
    },
    {
      field: 'showMine',
      label: t(translations.pages.leihbar.product.filter.myOffers()),
      toggle: {
        value: 'MINE',
        tooltip: t(translations.pages.leihbar.tooltips.myOffers()),
        color: 'default',
        stateHandler: showMineFilterState
      },
      hide: !ableToCreate
    }
  ];

  const columns = useMemo((): TypedGridColumns<Product> => {
    return [
      {
        flex: 2,
        headerName: t(translations.pages.leihbar.product.name()),
        field: 'name'
      },
      {
        flex: 2,
        headerName: t(translations.pages.leihbar.product.description()),
        field: 'description',
        renderCell: params => (
          <div
            className="fundings__description"
            dangerouslySetInnerHTML={{ __html: params.value }}
          />
        )
      },
      {
        flex: 1,
        headerName: t(translations.pages.leihbar.product.location()),
        field: 'location',
        renderCell: params => params.row.location!.name
      },
      {
        flex: 2,
        headerName: t(translations.pages.leihbar.product.categories()),
        field: 'categories',
        renderCell: params => <div>{params.row.categories!.map(c => c.name).join(', ')}</div>
      },
      {
        flex: 2,
        headerName: t(translations.pages.leihbar.product.domain()),
        field: 'lenderGroupId',
        renderCell: params =>
          entitlementGroups?.data.find(g => g.id === params.row.lenderGroupId)?.name
      }
    ];
  }, [entitlementGroups, t]);

  return (
    <Grid style={{ width: '100%', height: '100%', alignItems: 'center', alignSelf: 'center' }}>
      <TilesOrListView
        columns={columns}
        data={products?.data}
        filters={filters}
        onFilterChange={() => {}}
        onRowClick={product => {
          navigate(`/leihbar/product/${product.row.id}`, { state: { back: currentURL } });
        }}
        renderTile={product => <ProductCard product={product} />}
        controlElements={
          ableToCreate && (
            <Grid item>
              <CreateButton
                onClick={() => {
                  navigate('product/create');
                }}
              >
                {t(translations.common.create())}
              </CreateButton>
            </Grid>
          )
        }
        numberOfTilesShown={12}
        getRowHeight={() => 'auto'}
      />
    </Grid>
  );
};
